import { z } from 'zod';
import {
  AI_PROVIDERS,
  AREAS,
  FOCUS_PRESETS,
  JOURNAL_TYPES,
  MOOD_RATINGS,
} from './constants';

export const emailSchema = z.string().email().max(255);
export const passwordSchema = z.string().min(8).max(128);

export const inviteCodeSchema = z.string().min(1).max(64);

export const signupSchema = z.object({
  inviteCode: inviteCodeSchema,
  email: emailSchema,
  password: passwordSchema,
  name: z.string().min(1).max(64).optional(),
});
export type SignupInput = z.infer<typeof signupSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});
export type LoginInput = z.infer<typeof loginSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(1),
});

export const validateInviteSchema = z.object({
  inviteCode: inviteCodeSchema,
});

const areaSchema = z.string().min(1).max(32);

export const createTaskSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  area: areaSchema.default('Personal'),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const updateTaskSchema = createTaskSchema.partial().extend({
  status: z.enum(['in_progress', 'completed', 'overdue']).optional(),
});
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

export const taskTrendQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});

export const createJournalSchema = z.object({
  title: z.string().max(200).nullable().optional(),
  body: z.string().min(1).max(50000),
  type: z.enum(JOURNAL_TYPES).default('Journal'),
  area: areaSchema.nullable().optional(),
});
export type CreateJournalInput = z.infer<typeof createJournalSchema>;

export const updateJournalSchema = createJournalSchema.partial();
export type UpdateJournalInput = z.infer<typeof updateJournalSchema>;

export const journalListQuerySchema = z.object({
  type: z.enum(JOURNAL_TYPES).optional(),
  area: areaSchema.optional(),
  q: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});

export const aiTidySchema = z.object({
  body: z.string().min(1).max(50000),
  type: z.enum(JOURNAL_TYPES).default('Journal'),
});

export const aiConvertSchema = z.object({
  body: z.string().min(1).max(50000),
  type: z.enum(JOURNAL_TYPES).default('Journal'),
  area: areaSchema.nullable().optional(),
});

export const moodTagsSchema = z.array(z.string().min(1).max(32)).max(10).default([]);

export const createMoodSchema = z.object({
  rating: z.coerce.number().int().min(1).max(5),
  note: z.string().max(1000).nullable().optional(),
  tags: moodTagsSchema,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type CreateMoodInput = z.infer<typeof createMoodSchema>;

export const moodTrendQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});

export const startFocusSchema = z.object({
  duration: z.coerce.number().int().min(1).max(480),
  taskId: z.string().nullable().optional(),
});
export type StartFocusInput = z.infer<typeof startFocusSchema>;

export const endFocusSchema = z.object({
  status: z.enum(['completed', 'cancelled']),
  actualDuration: z.coerce.number().int().min(0).max(480).optional(),
});
export type EndFocusInput = z.infer<typeof endFocusSchema>;

export const updateSettingsSchema = z.object({
  aiProvider: z.enum(AI_PROVIDERS).optional(),
  aiApiKey: z.string().min(1).max(500).nullable().optional(),
  customAreas: z.array(areaSchema).max(20).optional(),
  defaultArea: areaSchema.optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

export const statsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
});

export const fixedAreaEnum = z.enum(AREAS);
export { FOCUS_PRESETS, MOOD_RATINGS };
